import { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { MapContainer, TileLayer, CircleMarker, Tooltip as LTooltip } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { PROVINCE_COORDS } from "../lib/provinceCoords";

const colorFor = (avg) => (avg >= 80 ? "#7F1D1D" : avg >= 60 ? "#EF4444" : avg >= 40 ? "#F59E0B" : "#10B981");

export default function HotspotPage() {
  const [regions, setRegions] = useState([]);

  useEffect(() => { api.get("/analytics/regions").then((r) => setRegions(r.data)).catch(() => {}); }, []);

  const maxTotal = useMemo(() => Math.max(1, ...regions.map((r) => r.total)), [regions]);
  const sorted = useMemo(() => [...regions].sort((a, b) => b.high_risk - a.high_risk || b.avg_score - a.avg_score), [regions]);

  return (
    <div className="p-8 space-y-6" data-testid="hotspot-page">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Peta Hotspot Klaim & Fraud</h1>
        <p className="text-sm text-muted-foreground mt-1">Sebaran klaim per provinsi — ukuran lingkaran = jumlah klaim, warna = rata-rata skor risiko</p>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <div className="xl:col-span-2 bg-card border border-border" data-testid="hotspot-map">
          <MapContainer center={[-2.5, 118]} zoom={5} style={{ height: "540px", width: "100%" }} scrollWheelZoom={true}>
            <TileLayer url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
              attribution='&copy; OpenStreetMap &copy; CARTO' />
            {regions.filter((r) => PROVINCE_COORDS[r.province]).map((r) => (
              <CircleMarker key={r.province} center={PROVINCE_COORDS[r.province]}
                radius={8 + (r.total / maxTotal) * 22}
                pathOptions={{ color: colorFor(r.avg_score), fillColor: colorFor(r.avg_score), fillOpacity: 0.55, weight: 1.5 }}>
                <LTooltip>
                  <div style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
                    <strong>{r.province}</strong><br />
                    Total klaim: {r.total} (Jiwa {r.jiwa} · Kendaraan {r.kendaraan})<br />
                    Rata-rata skor: {r.avg_score}<br />
                    Risiko tinggi/kritis: {r.high_risk}
                  </div>
                </LTooltip>
              </CircleMarker>
            ))}
          </MapContainer>
          <div className="flex items-center gap-5 px-4 py-3 border-t border-border text-xs text-muted-foreground">
            <span>Rata-rata skor:</span>
            {[["#10B981", "< 40 Rendah"], ["#F59E0B", "40-59 Sedang"], ["#EF4444", "60-79 Tinggi"], ["#7F1D1D", "≥ 80 Kritis"]].map(([c, l]) => (
              <span key={l} className="flex items-center gap-1.5"><span className="w-3 h-3 inline-block" style={{ background: c }} />{l}</span>
            ))}
          </div>
        </div>

        <div className="bg-card border border-border" data-testid="hotspot-table">
          <div className="px-4 py-3 border-b border-border">
            <h2 className="text-sm font-semibold">Peringkat Provinsi</h2>
          </div>
          <div className="max-h-[520px] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-card">
                <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
                  <th className="px-4 py-2 font-medium">Provinsi</th>
                  <th className="px-2 py-2 font-medium text-right">Klaim</th>
                  <th className="px-2 py-2 font-medium text-right">Skor</th>
                  <th className="px-3 py-2 font-medium text-right">Risiko Tinggi</th>
                </tr>
              </thead>
              <tbody>
                {sorted.length === 0 && <tr><td colSpan={4} className="px-4 py-8 text-center text-muted-foreground">Belum ada data klaim</td></tr>}
                {sorted.map((r) => (
                  <tr key={r.province} className="border-b border-border/60 last:border-0">
                    <td className="px-4 py-2.5 font-medium">{r.province}</td>
                    <td className="px-2 py-2.5 text-right font-mono">{r.total}</td>
                    <td className="px-2 py-2.5 text-right">
                      <span className="font-mono font-semibold" style={{ color: colorFor(r.avg_score) }}>{r.avg_score}</span>
                    </td>
                    <td className="px-3 py-2.5 text-right font-mono">{r.high_risk}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
